import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=auth'd
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("cinnabit_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("cinnabit_user", JSON.stringify(data));
    } catch (e) {
      localStorage.removeItem("cinnabit_token");
      localStorage.removeItem("cinnabit_user");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("cinnabit_token", data.access_token);
    localStorage.setItem("cinnabit_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("cinnabit_token", data.access_token);
    localStorage.setItem("cinnabit_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("cinnabit_token");
    localStorage.removeItem("cinnabit_user");
    setUser(false);
  };

  /** Used by Account Settings after a successful PUT /admin/account: swap in the
   * freshly-issued JWT + updated user record so the session stays valid without
   * the user having to log in again. */
  const setSession = (token, userObj) => {
    if (token) localStorage.setItem("cinnabit_token", token);
    if (userObj) {
      localStorage.setItem("cinnabit_user", JSON.stringify(userObj));
      setUser(userObj);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
